/**
 * Tipologia Fleuriet — LEGENDA da tela Caixa e Ciclo, nunca manchete.
 *
 * POR QUE NÃO NA PRIMEIRA DOBRA: "Tipo 4 de 6" não significa nada para um
 * diretor, e a primeira dobra é onde ele decide se o produto serve. O que vai
 * lá é frase; o tipo fica aqui, ao lado dos três números que o produzem.
 *
 * POR QUE "TIPO N" E DESCRIÇÃO PRÓPRIA: os nomes consagrados dos seis tipos
 * ("sólida", "insatisfatória", "péssima") vêm de Braga (1991) e Marques & Braga
 * (1995), que não foram conferidos contra o original. Num produto vendido a
 * consultor, um nome errado de categoria acadêmica é detectado na primeira
 * revisão — então usamos o número do tipo, que é indiscutível, e uma frase
 * nossa, que é nossa responsabilidade.
 *
 * POR QUE KANITZ E ALTMAN FICARAM DE FORA: Kanitz é, na prática, combinação
 * linear das liquidezes já exibidas ao lado, calibrado sobre 42 empresas de
 * 1972–74; Altman exige lucros acumulados, que o plano de contas não coleta.
 * Três classificadores colineares alimentados pelos mesmos 9 campos podem se
 * contradizer na frente do cliente. Fleuriet ficou porque é o único que mapeia
 * para uma ação: NCG contra CDG.
 */

/** Sinal contábil, com uma zona morta para não classificar ruído de arredondamento. */
const sinal = (valor, tolerancia = 0.01) => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return null;
  if (Math.abs(valor) <= tolerancia) return '+';
  return valor > 0 ? '+' : '-';
};

/** Os seis tipos, indexados pela tripla de sinais (CDG, NCG, T). */
const TIPOS = {
  '+-+': {
    tipo: 1,
    gravidade: 'excelente',
    frase: 'A operação gera caixa sozinha: o cliente paga antes de o fornecedor cobrar, e ainda sobra folga de longo prazo.'
  },
  '+++': {
    tipo: 2,
    gravidade: 'bom',
    frase: 'O giro precisa de dinheiro e o financiamento de longo prazo cobre com sobra. É a estrutura mais comum entre empresas saudáveis no Brasil.'
  },
  '++-': {
    tipo: 3,
    gravidade: 'atencao',
    frase: 'O giro precisa de mais dinheiro do que o longo prazo financia, e a diferença está sendo paga com dívida de curto prazo.'
  },
  '-+-': {
    tipo: 4,
    gravidade: 'critico',
    frase: 'Dívida de curto prazo está financiando ativo de longo prazo e ainda o giro. Uma recusa de renovação de crédito aperta o caixa imediatamente.'
  },
  '---': {
    tipo: 5,
    gravidade: 'critico',
    frase: 'A estrutura depende do prazo que os fornecedores concedem. Uma mudança de condição comercial vira problema de caixa na semana seguinte.'
  },
  '--+': {
    tipo: 6,
    gravidade: 'atencao',
    frase: 'A operação libera caixa (recebe antes de pagar) e é isso que sustenta a empresa, apesar de o longo prazo não cobrir o imobilizado.'
  }
};

/**
 * @param {{capitalDeGiro: number|null, ncg: number|null, saldoTesouraria: number|null}} entrada
 * @param {{balancosDisponiveis?: number}} contexto
 * @returns {null|{tipo, gravidade, frase, sinais, confianca, avisoDeConfianca, pesoNoIndice}}
 */
export const classificarFleuriet = (entrada, contexto = {}) => {
  const cdg = sinal(entrada?.capitalDeGiro);
  const ncg = sinal(entrada?.ncg);
  const tes = sinal(entrada?.saldoTesouraria);
  if (!cdg || !ncg || !tes) return null;

  const encontrado = TIPOS[`${cdg}${ncg}${tes}`];
  // Combinação aritmeticamente impossível (T = CDG − NCG restringe as triplas).
  // Preferimos não classificar a forçar o tipo mais parecido.
  if (!encontrado) return null;

  const balancos = contexto.balancosDisponiveis ?? 1;
  return {
    ...encontrado,
    sinais: { capitalDeGiro: cdg, ncg, saldoTesouraria: tes },
    // Com um único balanço não há como ver a estrutura se mover, e a tipologia
    // descreve um instante, não uma trajetória. Por isso ela é exibida com a
    // ressalva e NÃO pontua no Índice de Saúde.
    confianca: balancos >= 2 ? 'B' : 'C',
    avisoDeConfianca: balancos >= 2
      ? null
      : 'Leitura de um único balanço: descreve a posição de uma data, não a tendência.',
    pesoNoIndice: 0
  };
};

/**
 * A frase de posição do ciclo que vai na primeira dobra da tela Saúde.
 *
 * É a mesma informação do ciclo financeiro, dita como o dono fala: ele não
 * pensa "87 dias de ciclo", ele pensa "pago antes de receber".
 */
export const fraseDoCiclo = (cicloFinanceiroEmDias) => {
  if (cicloFinanceiroEmDias === null || cicloFinanceiroEmDias === undefined) return null;
  const dias = Math.round(cicloFinanceiroEmDias);
  if (dias > 0) return `Você paga o fornecedor ${dias} dias antes de receber do cliente.`;
  if (dias < 0) return `Você recebe do cliente ${Math.abs(dias)} dias antes de pagar o fornecedor.`;
  return 'Você paga o fornecedor no mesmo prazo em que recebe do cliente.';
};
